import { FormEvent, useContext, useEffect, useMemo, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { nord } from 'react-syntax-highlighter/dist/esm/styles/prism'
import { Checkbox, Dialog, FontIcon, IconButton, PrimaryButton, Stack, Text, TextField } from '@fluentui/react'
import { useBoolean } from '@fluentui/react-hooks'
import { Copy20Regular, Checkmark20Regular } from '@fluentui/react-icons'
import DOMPurify from 'dompurify'
import remarkGfm from 'remark-gfm'
import supersub from 'remark-supersub'
import { AskResponse, Citation, Feedback, historyMessageFeedback } from '../../api'
import { XSSAllowTags, XSSAllowAttributes } from '../../constants/sanatizeAllowables'
import { AppStateContext } from '../../state/AppProvider'
import { parseAnswer } from './AnswerParser'

import styles from './Answer.module.css'
import { useChatContext } from '../../state/ChatProvider'
import clsx from 'clsx'

interface Props {
  answer: AskResponse
  onCitationClicked: (citedDocument: Citation) => void
  onExectResultClicked: (answerId: string) => void
}

export const Answer = ({ answer, onCitationClicked, onExectResultClicked }: Props) => {
  const initializeAnswerFeedback = (answer: AskResponse) => {
    if (answer.message_id == undefined) return undefined
    if (answer.feedback == undefined) return undefined
    if (answer.feedback.split(',').length > 1) return Feedback.Negative
    if (Object.values(Feedback).includes(answer.feedback)) return answer.feedback
    return Feedback.Neutral
  }

  const [isRefAccordionOpen, { toggle: toggleIsRefAccordionOpen }] = useBoolean(false)
  const filePathTruncationLimit = 50

  const parsedAnswer = useMemo(() => parseAnswer(answer), [answer])
  const [chevronIsExpanded, setChevronIsExpanded] = useState(isRefAccordionOpen)
  const [feedbackState, setFeedbackState] = useState(initializeAnswerFeedback(answer))
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false)
  const [showReportInappropriateFeedback, setShowReportInappropriateFeedback] = useState(false)
  const [negativeFeedbackList, setNegativeFeedbackList] = useState<Feedback[]>([])
  const appStateContext = useContext(AppStateContext)
  const FEEDBACK_ENABLED =
    appStateContext?.state.frontendSettings?.feedback_enabled && appStateContext?.state.isCosmosDBAvailable?.cosmosDB
  const [isCopyConfirmationActive, setIsCopyConfirmationActive] = useState(false)
  const copyConfirmationTimeoutID = useRef<number | null>(null)
  const SANITIZE_ANSWER = appStateContext?.state.frontendSettings?.sanitize_answer
  const { activeCitation } = useChatContext()

  const hasCitations = useMemo(() => {
    return !!parsedAnswer?.citations.length
  }, [parsedAnswer])

  const handleChevronClick = () => {
    setChevronIsExpanded(!chevronIsExpanded)
    toggleIsRefAccordionOpen()
  }

  useEffect(() => {
    setChevronIsExpanded(isRefAccordionOpen)
  }, [isRefAccordionOpen])

  useEffect(() => {
    if (answer.message_id == undefined) return

    let currentFeedbackState
    if (appStateContext?.state.feedbackState && appStateContext?.state.feedbackState[answer.message_id]) {
      currentFeedbackState = appStateContext?.state.feedbackState[answer.message_id]
    } else {
      currentFeedbackState = initializeAnswerFeedback(answer)
    }
    setFeedbackState(currentFeedbackState)
  }, [appStateContext?.state.feedbackState, feedbackState, answer.message_id])

  const createCitationFilepath = (citation: Citation, index: number, truncate: boolean = false) => {
    let citationFilename = ''

    if (citation.filepath) {
      const part_i = citation.part_index ?? (citation.chunk_id ? parseInt(citation.chunk_id) + 1 : '')
      if (truncate && citation.filepath.length > filePathTruncationLimit) {
        const citationLength = citation.filepath.length
        citationFilename = `${citation.filepath.substring(0, 20)}...${citation.filepath.substring(citationLength - 20)} - Part ${part_i}`
      } else {
        citationFilename = `${citation.filepath} - Part ${part_i}`
      }
    } else if (citation.filepath && citation.reindex_id) {
      citationFilename = `${citation.filepath} - Part ${citation.reindex_id}`
    } else {
      citationFilename = `Citation ${index}`
    }
    return citationFilename
  }

  const onLikeResponseClicked = async () => {
    if (answer.message_id == undefined) return

    let newFeedbackState = feedbackState
    // Set or unset the thumbs up state
    if (feedbackState == Feedback.Positive) {
      newFeedbackState = Feedback.Neutral
    } else {
      newFeedbackState = Feedback.Positive
    }
    appStateContext?.dispatch({
      type: 'SET_FEEDBACK_STATE',
      payload: { answerId: answer.message_id, feedback: newFeedbackState }
    })
    setFeedbackState(newFeedbackState)

    // Update message feedback in db
    await historyMessageFeedback(answer.message_id, newFeedbackState)
  }

  const onDislikeResponseClicked = async () => {
    if (answer.message_id == undefined) return

    let newFeedbackState = feedbackState
    if (feedbackState === undefined || feedbackState === Feedback.Neutral || feedbackState === Feedback.Positive) {
      newFeedbackState = Feedback.Negative
      setFeedbackState(newFeedbackState)
      setIsFeedbackDialogOpen(true)
    } else {
      // Reset negative feedback to neutral
      newFeedbackState = Feedback.Neutral
      setFeedbackState(newFeedbackState)
      await historyMessageFeedback(answer.message_id, Feedback.Neutral)
    }
    appStateContext?.dispatch({
      type: 'SET_FEEDBACK_STATE',
      payload: { answerId: answer.message_id, feedback: newFeedbackState }
    })
  }

  const updateFeedbackList = (ev?: FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
    if (answer.message_id == undefined) return
    const selectedFeedback = (ev?.target as HTMLInputElement)?.id as Feedback

    let feedbackList = negativeFeedbackList.slice()
    if (checked) {
      feedbackList.push(selectedFeedback)
    } else {
      feedbackList = feedbackList.filter(f => f !== selectedFeedback)
    }

    setNegativeFeedbackList(feedbackList)
  }

  const onSubmitNegativeFeedback = async () => {
    if (answer.message_id == undefined) return
    await historyMessageFeedback(answer.message_id, negativeFeedbackList.join(','))
    resetFeedbackDialog()
  }

  const resetFeedbackDialog = () => {
    setIsFeedbackDialogOpen(false)
    setShowReportInappropriateFeedback(false)
    setNegativeFeedbackList([])
  }

  const UnhelpfulFeedbackContent = () => {
    return (
      <>
        <div style={{ margin: '10px 0' }}>Why wasn't this response helpful?</div>
        <Stack tokens={{ childrenGap: 11 }}>
          <Checkbox
            label="Inaccurate or irrelevant"
            id={Feedback.InaccurateOrIrrelevant}
            defaultChecked={negativeFeedbackList.includes(Feedback.InaccurateOrIrrelevant)}
            onChange={updateFeedbackList}></Checkbox>
          <Checkbox
            label="The response is not from my data"
            id={Feedback.OutOfScope}
            defaultChecked={negativeFeedbackList.includes(Feedback.OutOfScope)}
            onChange={updateFeedbackList}></Checkbox>
          <Checkbox
            label="Citations are wrong"
            id={Feedback.WrongCitation}
            defaultChecked={negativeFeedbackList.includes(Feedback.WrongCitation)}
            onChange={updateFeedbackList}></Checkbox>
          <Checkbox
            label="Citations are missing"
            id={Feedback.MissingCitation}
            defaultChecked={negativeFeedbackList.includes(Feedback.MissingCitation)}
            onChange={updateFeedbackList}></Checkbox>
          <Checkbox
            label="Other"
            id={Feedback.OtherUnhelpful}
            defaultChecked={negativeFeedbackList.includes(Feedback.OtherUnhelpful)}
            onChange={updateFeedbackList}></Checkbox>
          {negativeFeedbackList.includes(Feedback.OtherUnhelpful) && <TextField
            className={styles.otherFeedbackInput}
            styles={{
              root: {
                height: '100px'
              },
              wrapper: { height: '100%' },
              fieldGroup: {
                height: '100%',
                borderRadius: '8px',
                border: '0.5px solid rgba(161, 161, 161, 0.65)',
                background: '#FFF',
              }
            }}
            multiline
            resizable={false}
            maxLength={300}
          />}
        </Stack>
        <div onClick={() => setShowReportInappropriateFeedback(true)} style={{ color: '#0051a0', cursor: 'pointer' }}>
          Report inappropriate content
        </div>
      </>
    )
  }

  const ReportInappropriateFeedbackContent = () => {
    return (
      <>
        <div style={{ margin: '10px 0' }}>
          The content is <span style={{ color: 'red' }}>*</span>
        </div>
        <Stack tokens={{ childrenGap: 11 }}>
          <Checkbox
            label="Hate speech, stereotyping, demeaning"
            id={Feedback.HateSpeech}
            defaultChecked={negativeFeedbackList.includes(Feedback.HateSpeech)}
            onChange={updateFeedbackList}></Checkbox>
          <Checkbox
            label="Violent: glorification of violence, self-harm"
            id={Feedback.Violent}
            defaultChecked={negativeFeedbackList.includes(Feedback.Violent)}
            onChange={updateFeedbackList}></Checkbox>
          <Checkbox
            label="Sexual: explicit content, grooming"
            id={Feedback.Sexual}
            defaultChecked={negativeFeedbackList.includes(Feedback.Sexual)}
            onChange={updateFeedbackList}></Checkbox>
          <Checkbox
            label="Manipulative: devious, emotional, pushy, bullying"
            defaultChecked={negativeFeedbackList.includes(Feedback.Manipulative)}
            id={Feedback.Manipulative}
            onChange={updateFeedbackList}></Checkbox>
          <Checkbox
            label="Other"
            id={Feedback.OtherHarmful}
            defaultChecked={negativeFeedbackList.includes(Feedback.OtherHarmful)}
            onChange={updateFeedbackList}></Checkbox>
        </Stack>
      </>
    )
  }

  const components = {
    code({ node, ...props }: { node: any;[key: string]: any }) {
      let language
      if (props.className) {
        const match = props.className.match(/language-(\w+)/)
        language = match ? match[1] : undefined
      }
      const codeString = node.children[0].value ?? ''
      return (
        <SyntaxHighlighter style={nord} language={language} PreTag="div" {...props}>
          {codeString}
        </SyntaxHighlighter>
      )
    }
  }
  async function copyHtmlToClipboard() {
    try {
      const content = typeof answer.answer == 'string' ? answer.answer : answer.answer?.join(' ') ?? ''
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/plain': new Blob([content], { type: 'text/plain' })
        })
      ])
    } catch (err) {
      console.error('Failed to copy HTML: ', err)
    }
  }

  const handleCopy = () => {
    copyHtmlToClipboard().then(() => {
      if (copyConfirmationTimeoutID.current !== null) {
        clearTimeout(copyConfirmationTimeoutID.current)
        copyConfirmationTimeoutID.current = null
      }
      setIsCopyConfirmationActive(true)
      copyConfirmationTimeoutID.current = window.setTimeout(() => setIsCopyConfirmationActive(false), 3000)
    })
  }
  return (
    <>
      <Stack className={styles.answerContainer} tabIndex={0}>
        <Stack.Item style={{ padding: '0 14px', width: '100%' }}>
          <Stack.Item>
            <Stack horizontal grow>
              <Stack.Item grow>
                {parsedAnswer && (
                  <ReactMarkdown
                    linkTarget="_blank"
                    remarkPlugins={[remarkGfm, supersub]}
                    children={
                      SANITIZE_ANSWER
                        ? DOMPurify.sanitize(parsedAnswer?.markdownFormatText, {
                          ALLOWED_TAGS: XSSAllowTags,
                          ALLOWED_ATTR: XSSAllowAttributes
                        })
                        : parsedAnswer?.markdownFormatText
                    }
                    className={styles.answerText}
                    components={components}
                  />
                )}
              </Stack.Item>
            </Stack>
          </Stack.Item>
          {parsedAnswer?.generated_chart !== null && (
            <Stack className={styles.answerContainer}>
              <Stack.Item grow>
                <img src={`data:image/png;base64, ${parsedAnswer?.generated_chart}`} />
              </Stack.Item>
            </Stack>
          )}
          {answer.message_id && (
            <Stack horizontal className={styles.answerFooter}>
              <Stack.Item style={{ marginLeft: 'auto' }}>
                {isCopyConfirmationActive ? (
                  <Checkmark20Regular
                    aria-hidden="false"
                    aria-label="Copied this response"
                    style={{ color: 'darkgreen' }}
                  />
                ) : (
                  <Copy20Regular
                    aria-hidden="false"
                    aria-label="Copy this response"
                    onClick={() => handleCopy()}
                    style={{ cursor: 'pointer' }}
                  />
                )}
              </Stack.Item>
              {!!answer.exec_results?.length && (
                <Stack.Item onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? toggleIsRefAccordionOpen() : null)}>
                  <Stack style={{ width: '100%' }}>
                    <Stack horizontal horizontalAlign="start" verticalAlign="center">
                      <Text
                        className={styles.accordionTitle}
                        onClick={() => onExectResultClicked(answer.message_id ?? '')}
                        aria-label="Open Intents"
                        tabIndex={0}
                        role="button">
                        <span>Show Intents</span>
                      </Text>
                      <FontIcon
                        className={styles.accordionIcon}
                        onClick={handleChevronClick}
                        iconName={'ChevronRight'}
                      />
                    </Stack>
                  </Stack>
                </Stack.Item>
              )}
            </Stack>
          )}
          {answer.message_id && hasCitations && (
            <div className={styles.citationWrapper}>
              <div className={styles.citationsTitleContainer}>
                <div className={styles.citationsTitle}>
                  <span className={styles.citationsCount}>{parsedAnswer!.citations.length}</span>
                  <span>{parsedAnswer!.citations.length > 1 ? 'References' : 'Reference'}</span>
                </div>
              </div>
              {parsedAnswer?.citations.map((citation, idx) => {
                return (
                  <span
                    title={createCitationFilepath(citation, ++idx)}
                    tabIndex={0}
                    role="link"
                    key={idx}
                    onClick={() => onCitationClicked(citation)}
                    onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? onCitationClicked(citation) : null)}
                    className={clsx(styles.citationContainer, { [styles.selectedCitation]: citation.id === activeCitation?.citation.id && answer.message_id === activeCitation?.answerId })}
                    aria-label={createCitationFilepath(citation, idx)}>
                    <div className={styles.citationNumber}>{idx}</div>
                    {createCitationFilepath(citation, idx, true)}
                  </span>
                )
              })}
            </div>
          )}
        </Stack.Item>
        {answer.message_id && (
          <Stack.Item className={styles.footerContainer}>
            <div className={styles.answerDisclaimer}>
              <span>AI-generated content may be incorrect. For more information
                <a href='https://www.imf.org/en/About/privacy-notice' target="_blank" rel="noopener noreferrer">click here</a>
              </span>
            </div>
            {FEEDBACK_ENABLED && answer.message_id !== undefined && (
              <Stack
                horizontal
                horizontalAlign="end"
                verticalAlign="center"
                tokens={{ childrenGap: 5 }}
                className={styles.feedbackContainer}>
                <span className={styles.feedbackLabel}>Feedback on this response</span>
                <IconButton
                  aria-hidden="false"
                  aria-label="Like this response"
                  iconProps={{ iconName: 'LikeIcon' }}
                  onClick={() => onLikeResponseClicked()}
                  styles={{
                    root: {
                      width: '24px',
                      height: '24px',
                      borderRadius: '4px',
                      background: feedbackState === Feedback.Positive ||
                        appStateContext?.state.feedbackState[answer.message_id] === Feedback.Positive ? '#dedede' : ''
                    },
                    rootHovered: {
                      background: '#dedede'
                    }
                  }} />
                <span className={styles.thumbsUpSeparator}></span>
                <IconButton
                  aria-hidden="false"
                  aria-label="Dislike this response"
                  iconProps={{ iconName: 'DislikeIcon' }}
                  onClick={() => onDislikeResponseClicked()}
                  styles={{
                    root: {
                      width: '24px',
                      height: '24px',
                      borderRadius: '4px',
                      background: feedbackState !== Feedback.Positive &&
                        feedbackState !== Feedback.Neutral &&
                        feedbackState !== undefined ? '#dedede' : ''
                    },
                    rootHovered: {
                      background: '#dedede'
                    }
                  }} />
              </Stack>
            )}
          </Stack.Item>
        )}
      </Stack >
      <Dialog
        onDismiss={() => {
          resetFeedbackDialog()
          setFeedbackState(Feedback.Neutral)
        }}
        hidden={!isFeedbackDialogOpen}
        styles={{
          main: [
            {
              selectors: {
                ['@media (min-width: 480px)']: {
                  maxWidth: '600px',
                  background: '#FFFFFF',
                  boxShadow: '0px 14px 28.8px rgba(0, 0, 0, 0.24), 0px 0px 8px rgba(0, 0, 0, 0.2)',
                  borderRadius: '8px',
                  maxHeight: '600px',
                  minHeight: '100px'
                }
              }
            }
          ]
        }}
        dialogContentProps={{
          title: 'Submit Feedback',
          showCloseButton: true,
          styles: {
            title: {
              color: '#000',
              fontSize: '20px',
              fontStyle: 'normal',
              fontWeight: '700',
              lineHeight: '28px',
              padding: '24px 46px 14px 24px'
            }
          }
        }}>
        <Stack tokens={{ childrenGap: 10 }}>
          <div>Your feedback will improve this experience.</div>

          {!showReportInappropriateFeedback ? <UnhelpfulFeedbackContent /> : <ReportInappropriateFeedbackContent />}

          <div style={{ marginTop: "6px" }}>By pressing submit, your feedback will be visible to the application owner.</div>

          <PrimaryButton
            disabled={negativeFeedbackList.length < 1}
            onClick={onSubmitNegativeFeedback}
            styles={{ root: { marginTop: '22px !important' }, rootDisabled: { background: 'transparent' } }}>
            Submit
          </PrimaryButton>
        </Stack>
      </Dialog>
    </>
  )
}
